import { DEFAULT_TIMEZONE } from "@dream/config/constants";
import { localDateKey, parseFocusDate } from "@dream/domain";
import {
  Button,
  IconBolt,
  IconCalendar,
  IconChevronLeft,
  IconClients,
  IconClose,
  IconFolder,
  IconLogout,
  IconMenu,
  IconPublish,
  IconSearch,
  IconSettings,
} from "@dream/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { DateTime } from "luxon";
import {
  type FormEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api, signOut as apiSignOut, unwrap } from "../api/client.ts";
import { nextExclusiveIds } from "../components/calendar-chrome.ts";
import { CalendarList } from "../components/calendar-list.tsx";
import { CommandPalette } from "../components/command-palette.tsx";
import { LegalNav } from "../components/cookie-consent.tsx";
import { DreamLogo } from "../components/dream-logo.tsx";
import { HelpDialog } from "../components/help-dialog.tsx";
import { MiniMonth } from "../components/mini-month.tsx";
import { ThemeToggle } from "../components/theme-toggle.tsx";
import { useAdminCatalog } from "../hooks/use-calendar.ts";
import { mergeCalendarSearch, parseSearchParamsRecord } from "../lib/calendar-url.ts";
import { displayNameFromParts, initials } from "../lib/people.ts";
import { usePreferences } from "../preferences/provider.tsx";
import { type CalendarSearch, calendarSearch, validateCalendarSearch } from "../router.tsx";
import { AdminChromeProvider } from "./admin-chrome.tsx";

/**
 * Casca do painel — chrome da frame Ofative (sidebar 288, mini mês, Meus
 * calendários, paleta ⌘K, G then S).
 */

interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
}

const NAV: NavItem[] = [
  { to: "/calendario", label: "Calendário", icon: <IconCalendar className="size-[18px]" /> },
  { to: "/moon", label: "Moon", icon: <IconBolt className="size-[18px]" /> },
  { to: "/clientes", label: "Clientes", icon: <IconClients className="size-[18px]" /> },
  { to: "/projetos", label: "Projetos", icon: <IconFolder className="size-[18px]" /> },
  { to: "/publicacoes", label: "Publicações", icon: <IconPublish className="size-[18px]" /> },
  { to: "/configuracoes", label: "Configurações", icon: <IconSettings className="size-[18px]" /> },
];

const SIDEBAR_KEY = "dream.sidebar.collapsed";

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
}

function persistSidebarCollapsed(value: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_KEY, value ? "1" : "0");
  } catch {
    /* quota */
  }
}

function navSearch(to: string) {
  if (to === "/calendario") return calendarSearch();
  if (to === "/configuracoes") return {};
  if (to === "/moon") return {};
  return undefined;
}

export function AdminShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerShown, setDrawerShown] = useState(false);
  const [drawerLeaving, setDrawerLeaving] = useState(false);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [signingOut, setSigningOut] = useState(false);
  const [query, setQuery] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isSettings = pathname.startsWith("/configuracoes");
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const { prefs } = usePreferences();
  const catalog = useAdminCatalog();

  const focusDate = useMemo(() => {
    const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
    return parseFocusDate(params.get("date") ?? undefined, DEFAULT_TIMEZONE);
  }, [searchStr]);

  const activeClientIds = useMemo(() => {
    const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
    const raw = params.get("clients");
    return raw === null || raw === "" ? [] : raw.split(",").filter((id) => id !== "");
  }, [searchStr]);

  const activeAssigneeIds = useMemo(() => {
    const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
    const raw = params.get("assignees");
    return raw === null || raw === "" ? [] : raw.split(",").filter((id) => id !== "");
  }, [searchStr]);

  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.api.me.get()) as { email: string; userId: string },
  });
  const sessionEmail = me.data?.email;

  const signOut = useCallback(async (): Promise<void> => {
    setSigningOut(true);
    try {
      await apiSignOut();
    } finally {
      queryClient.clear();
      setSigningOut(false);
      void navigate({ to: "/login", search: { redirect: undefined } });
    }
  }, [navigate, queryClient]);

  function calendarNavigate(patch: Partial<CalendarSearch>): void {
    const search = pathname.startsWith("/calendario")
      ? mergeCalendarSearch(validateCalendarSearch(parseSearchParamsRecord(searchStr)), patch)
      : calendarSearch(patch);
    void navigate({ to: "/calendario", search });
  }

  function goToDate(date: Date): void {
    calendarNavigate({ date: localDateKey(date, DEFAULT_TIMEZONE) });
  }

  function shiftMonth(delta: number): void {
    goToDate(
      DateTime.fromJSDate(focusDate, { zone: DEFAULT_TIMEZONE }).plus({ months: delta }).toJSDate(),
    );
  }

  function onSearch(event: FormEvent): void {
    event.preventDefault();
    const q = query.trim();
    setDrawerOpen(false);
    calendarNavigate({ q: q === "" ? undefined : q });
  }

  function toggleClient(clientId: string): void {
    const next = nextExclusiveIds(activeClientIds, clientId);
    setDrawerOpen(false);
    calendarNavigate({
      clients: next.length === 0 ? undefined : next.join(","),
    });
  }

  function toggleMyTasks(): void {
    const userId = me.data?.userId;
    if (userId === undefined) return;
    const next = nextExclusiveIds(activeAssigneeIds, userId);
    setDrawerOpen(false);
    calendarNavigate({
      assignees: next.length === 0 ? undefined : next.join(","),
    });
  }

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      persistSidebarCollapsed(next);
      return next;
    });
  }, []);

  const openNewDeliverable = useCallback(() => {
    if (pathname.startsWith("/calendario")) {
      void navigate({
        from: "/calendario",
        search: (prev) => ({ ...prev, nova: "1" }),
      });
      return;
    }
    void navigate({
      to: "/calendario",
      search: calendarSearch({ nova: "1" }),
    });
  }, [navigate, pathname]);

  useEffect(() => {
    if (drawerOpen) {
      setDrawerShown(true);
      setDrawerLeaving(false);
      return;
    }
    if (!drawerShown) return;
    setDrawerLeaving(true);
    const timer = window.setTimeout(() => {
      setDrawerShown(false);
      setDrawerLeaving(false);
    }, 160);
    return () => window.clearTimeout(timer);
  }, [drawerOpen, drawerShown]);

  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  useEffect(() => {
    let awaitingS = false;
    let timer = 0;
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        target.closest("input, textarea, select, [contenteditable='true']") !== null;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
        event.preventDefault();
        toggleCollapsed();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }

      if (typing) return;

      if (event.key.toLowerCase() === "g" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        awaitingS = true;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          awaitingS = false;
        }, 800);
        return;
      }

      if (awaitingS && event.key.toLowerCase() === "s") {
        event.preventDefault();
        awaitingS = false;
        void navigate({ to: "/configuracoes", search: {} });
        return;
      }

      if (event.key.toLowerCase() === "n" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        openNewDeliverable();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(timer);
    };
  }, [navigate, openNewDeliverable, toggleCollapsed]);

  const displayName = displayNameFromParts(
    prefs.firstName,
    prefs.lastName,
    prefs.displayName.trim() !== ""
      ? prefs.displayName.trim()
      : (sessionEmail?.split("@")[0] ?? "Equipe"),
  );
  const sidebarWidth = collapsed ? "var(--dream-sidebar-collapsed)" : "var(--dream-sidebar-width)";

  const chrome = useMemo(
    () => ({
      openPalette: () => setPaletteOpen(true),
      openHelp: () => setHelpOpen(true),
      signOut,
    }),
    [signOut],
  );

  const clients = catalog.clients as Array<{
    id: string;
    name: string;
    color: string;
    logoUrl?: string | null;
  }>;

  const myTasksProps =
    me.data?.userId === undefined
      ? null
      : {
          active: activeAssigneeIds.length === 1 && activeAssigneeIds[0] === me.data.userId,
          initials: initials(displayName),
          onToggle: toggleMyTasks,
        };

  const calendarLinkSearch = pathname.startsWith("/calendario")
    ? validateCalendarSearch(parseSearchParamsRecord(searchStr))
    : calendarSearch();

  return (
    <AdminChromeProvider value={chrome}>
      <div className="flex h-full min-h-0 flex-col overflow-x-clip bg-[var(--color-surface)]">
        <a href="#conteudo" className="dream-skip-link">
          Ir para o conteúdo
        </a>

        <div className="flex min-h-0 flex-1">
          {!isSettings && (
            <aside
              aria-label="Menu do painel"
              className="relative z-20 hidden h-full min-h-0 shrink-0 overflow-hidden border-r border-[var(--color-line)] bg-[var(--color-surface-subtle)] transition-[width] duration-[var(--dream-duration-base)] ease-[var(--dream-ease-standard)] md:flex md:flex-col"
              data-collapsed={collapsed ? "true" : "false"}
              style={{ width: sidebarWidth }}
            >
              <div
                className={
                  collapsed
                    ? "flex shrink-0 flex-col items-center gap-1 px-1.5 py-2"
                    : "flex h-[var(--dream-topbar-height)] shrink-0 items-center gap-2 px-3"
                }
              >
                <div
                  className={collapsed ? "flex justify-center" : "min-w-0 flex-1 overflow-hidden"}
                >
                  <DreamLogo compact={collapsed} />
                </div>
                <button
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
                  className="dream-icon-btn relative z-10 size-10 shrink-0"
                  onClick={toggleCollapsed}
                  title={collapsed ? "Expandir menu (Ctrl+B)" : "Recolher menu (Ctrl+B)"}
                  type="button"
                >
                  <IconChevronLeft
                    className={`size-4 transition-transform duration-[var(--dream-duration-base)] ${collapsed ? "rotate-180" : ""}`}
                  />
                </button>
              </div>

              <div className="dream-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
                {!collapsed && (
                  <div className="dream-enter px-3 pb-3">
                    <MiniMonth
                      focusDate={focusDate}
                      timezone={DEFAULT_TIMEZONE}
                      weekStartsOn={prefs.weekStartsOn}
                      onSelect={goToDate}
                      onShiftMonth={shiftMonth}
                    />
                  </div>
                )}

                {!collapsed && (
                  <form className="px-3 pb-3" onSubmit={onSearch}>
                    <label className="relative block">
                      <span className="sr-only">Buscar entregas</span>
                      <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-ink-muted)]" />
                      <input
                        className="h-10 w-full rounded-lg border-0 bg-[var(--color-surface)] pl-9 pr-3 text-[14px] font-medium text-[var(--color-ink)] placeholder:text-[var(--color-ink-muted)] focus:outline-none focus:shadow-[var(--dream-focus-ring)]"
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar entregas..."
                        type="search"
                        value={query}
                      />
                    </label>
                  </form>
                )}

                {!collapsed && (
                  <CalendarList
                    activeIds={activeClientIds}
                    clients={clients}
                    myTasks={myTasksProps}
                    onToggle={toggleClient}
                  />
                )}

                <nav aria-label="Navegação principal" className="space-y-0.5 px-1.5 pb-2">
                  {!collapsed && (
                    <p className="px-2.5 pb-1 pt-2 text-[14px] font-semibold text-[var(--color-ink-muted)]">
                      Painel
                    </p>
                  )}
                  {NAV.map((item) => {
                    const active = pathname.startsWith(item.to);
                    const link = (
                      <Link
                        key={item.to}
                        aria-current={active ? "page" : undefined}
                        aria-label={item.label}
                        className={[
                          "flex items-center gap-2.5 rounded-lg py-2 text-[14px] tracking-[0]",
                          collapsed ? "justify-center px-0" : "px-2.5",
                          "transition-[color,background-color,box-shadow,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                          active
                            ? "bg-[var(--color-surface)] font-semibold text-[var(--color-ink)] shadow-[var(--shadow-xs)]"
                            : "font-medium text-[var(--color-ink-secondary)] hover:translate-x-px hover:bg-[var(--color-surface)]/80 hover:text-[var(--color-ink)]",
                        ].join(" ")}
                        search={item.to === "/calendario" ? calendarLinkSearch : navSearch(item.to)}
                        to={item.to}
                      >
                        <span className={`shrink-0 ${active ? "text-brand-600" : ""}`}>
                          {item.icon}
                        </span>
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </Link>
                    );
                    return collapsed ? (
                      <Tooltip key={item.to}>
                        <TooltipTrigger asChild>
                          <div className="flex justify-center">{link}</div>
                        </TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <div key={item.to}>{link}</div>
                    );
                  })}
                </nav>
              </div>

              <div
                className={`shrink-0 border-t border-[var(--color-line)] ${collapsed ? "p-1.5" : "p-3"}`}
              >
                <div className={collapsed ? "mb-1 flex justify-center" : "mb-2"}>
                  <ThemeToggle compact={collapsed} hintSide={collapsed ? "right" : "bottom"} />
                </div>
                {collapsed ? (
                  <div className="flex flex-col items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          aria-label="Configurações"
                          className="flex size-10 items-center justify-center rounded-full bg-brand-50 text-[0.75rem] font-bold text-brand-600 transition-[transform,background-color] duration-[var(--dream-duration-fast)] hover:bg-brand-100"
                          search={{}}
                          to="/configuracoes"
                        >
                          {displayName.slice(0, 1).toUpperCase()}
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right">{displayName}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          aria-label={signingOut ? "Saindo..." : "Sair"}
                          className="dream-icon-btn size-10 disabled:opacity-60"
                          disabled={signingOut}
                          onClick={() => void signOut()}
                          type="button"
                        >
                          <IconLogout className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">Sair</TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[0.75rem] font-bold text-brand-600"
                    >
                      {displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[16px] font-bold capitalize text-[var(--color-ink)]">
                        {displayName}
                      </span>
                      <span className="block truncate text-[12px] font-medium text-[var(--color-ink-muted)]">
                        {sessionEmail ?? ""}
                      </span>
                    </span>
                    <button
                      aria-label={signingOut ? "Saindo..." : "Sair"}
                      className="dream-icon-btn size-10 shrink-0 disabled:opacity-60"
                      disabled={signingOut}
                      onClick={() => void signOut()}
                      type="button"
                    >
                      <IconLogout className="size-4" />
                    </button>
                  </div>
                )}
                {!collapsed && <LegalNav className="mt-2 px-0.5" />}
              </div>
            </aside>
          )}

          {!isSettings && drawerShown && (
            <div className="fixed inset-0 z-[80] md:hidden">
              <button
                type="button"
                aria-label="Fechar navegação"
                onClick={() => setDrawerOpen(false)}
                className={`${drawerLeaving ? "dream-overlay-out" : "dream-overlay-in"} absolute inset-0 bg-[rgb(15_23_42_/_0.45)]`}
              />
              <div
                className={`${drawerLeaving ? "dream-drawer-out" : "dream-drawer-in"} absolute inset-y-0 left-0 flex w-[min(20rem,calc(100vw-2.5rem))] flex-col border-r border-[var(--color-line)] bg-[var(--color-surface-subtle)] pb-[env(safe-area-inset-bottom)] shadow-xl`}
              >
                <div className="flex h-14 items-center justify-between px-3">
                  <DreamLogo />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Fechar menu"
                    onClick={() => setDrawerOpen(false)}
                  >
                    <IconClose className="size-4" />
                  </Button>
                </div>
                <div className="dream-scroll min-h-0 flex-1 overflow-y-auto">
                  <div className="px-3 pb-3">
                    <MiniMonth
                      focusDate={focusDate}
                      timezone={DEFAULT_TIMEZONE}
                      weekStartsOn={prefs.weekStartsOn}
                      onSelect={(date) => {
                        goToDate(date);
                        setDrawerOpen(false);
                      }}
                      onShiftMonth={shiftMonth}
                    />
                  </div>
                  <form onSubmit={onSearch} className="px-3 pb-3">
                    <label className="relative block">
                      <span className="sr-only">Buscar entregas</span>
                      <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-ink-muted)]" />
                      <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar entregas..."
                        className="h-10 w-full rounded-lg border-0 bg-[var(--color-surface)] pl-9 pr-3 text-[14px] font-medium text-[var(--color-ink)] placeholder:text-[var(--color-ink-muted)] focus:outline-none focus:shadow-[var(--dream-focus-ring)]"
                      />
                    </label>
                  </form>
                  <CalendarList
                    clients={clients}
                    activeIds={activeClientIds}
                    myTasks={myTasksProps}
                    onToggle={toggleClient}
                  />
                  <nav aria-label="Navegação principal" className="space-y-0.5 p-2">
                    {NAV.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        search={item.to === "/calendario" ? calendarLinkSearch : navSearch(item.to)}
                        onClick={() => setDrawerOpen(false)}
                        aria-current={pathname.startsWith(item.to) ? "page" : undefined}
                        className={[
                          "flex min-h-11 items-center gap-2.5 rounded-lg px-2.5 py-2 text-[14px] tracking-[0]",
                          "transition-[color,background-color,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                          pathname.startsWith(item.to)
                            ? "bg-[var(--color-surface)] font-semibold text-[var(--color-ink)] shadow-[var(--shadow-xs)]"
                            : "font-medium text-[var(--color-ink-secondary)] hover:translate-x-px hover:bg-[var(--color-surface)]/80 hover:text-[var(--color-ink)]",
                        ].join(" ")}
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    ))}
                  </nav>
                </div>
                <div className="space-y-1 border-t border-[var(--color-line)] p-2">
                  <ThemeToggle />
                  <LegalNav className="px-2.5 py-1" />
                  <Button
                    variant="ghost"
                    onClick={() => void signOut()}
                    className="w-full justify-start"
                  >
                    Sair
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex min-w-0 flex-1 flex-col">
            {!isSettings && (
              <header className="relative z-30 flex min-h-14 shrink-0 items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-surface)] px-3 pt-[env(safe-area-inset-top)] md:hidden">
                <Button
                  aria-label="Abrir menu"
                  className="size-11"
                  onClick={() => setDrawerOpen(true)}
                  size="icon"
                  variant="ghost"
                >
                  <IconMenu className="size-5" />
                </Button>
                <DreamLogo compact />
                <div className="ml-auto flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Buscar"
                    onClick={() => setPaletteOpen(true)}
                  >
                    <IconSearch className="size-4" />
                  </Button>
                  <ThemeToggle compact />
                </div>
              </header>
            )}

            <main
              id="conteudo"
              data-app-main
              key={pathname}
              className="dream-page-enter flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden"
            >
              <Outlet />
            </main>
          </div>
        </div>

        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          onCreate={() => {
            setPaletteOpen(false);
            openNewDeliverable();
          }}
        />
        <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      </div>
    </AdminChromeProvider>
  );
}
